import React, { useContext } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { EmployeeIcon } from './EmployeeIcon';
import {
  SHIFT_ALLOCATION_ID_PREFIX,
  SHIFT_ALLOCATION_ID_SUFFIX,
  DROPPABLE_ID_SEPARATOR,
  SCAN_ARMED_OUTLINE,
  SCAN_ARMED_GLOW
} from '../../utils/constants';
import { EmptyEmployeeIcon } from './EmptyEmployeeIcon';
import styles from './styles.module.css';
import {
  clusterAssignmentHeaderFormatter,
  createSlotIdGenerator,
  getHeadcountForProcessPaths,
  getProcessCardHeaderColor
} from './utils';
import {
  ALLOCATION_TYPE,
  PROCESS_PATHS,
  PROCESS_TYPE,
  TRAINING_ALLOCATION_TYPES,
  EDITABLE_SLOT_LABEL_CARDS
} from '../data';
import { ShiftAllocationContext } from '../context/ShiftAllocationContext';
import { SET_SLOT_LABEL } from '../action/ShiftAllocationAction';

const EXCLUSIVE_PROCESS_DETAIL_CARDS = [PROCESS_PATHS.TRAINEE, PROCESS_PATHS.YARD_MARSHALL];

const validProcessToRenderProcessDetailCard = (renderList, boardEntry) => {
  return (
    renderList.length === 0 &&
    boardEntry.processPathEntries.length > 0 &&
    !EXCLUSIVE_PROCESS_DETAIL_CARDS.includes(boardEntry.title)
  );
};

export const ProcessDetailCard = ({ boardEntry, associateInfo }) => {
  const { shiftAllocationDispatch, setIsBoardUpdated, armedScanCard, setArmedScanCard, armedScanSlot } =
    useContext(ShiftAllocationContext);

  // Per-slot editable labels are enabled for a configured set of cards (Diverter, Straightener,
  // IB Dock, Sort PS, Stow Bag Replenishment). We match on the card's display title OR any of its
  // underlying process paths, because a card's header text can differ from the canonical process
  // name (e.g. header "Straightener" vs process "Auto Divert Straightener"). Labels are keyed by a
  // stable slotId "<boardEntryId>::<process>::<ordinal>" — the slot's position within its process
  // path — so a label stays with the physical position on the card across drag-and-drop.
  const isLabelEditable =
    EDITABLE_SLOT_LABEL_CARDS.has(boardEntry.title) ||
    (boardEntry.processPathEntries || []).some((ppe) => ppe && EDITABLE_SLOT_LABEL_CARDS.has(ppe.process));
  const slotLabels = boardEntry.slotLabels || {};

  const makeOnEditLabel = (slotId) => (text) => {
    shiftAllocationDispatch({
      type: SET_SLOT_LABEL,
      data: { boardEntryId: boardEntry.id, slotId, label: text }
    });
    setIsBoardUpdated(true);
  };

  // Scan-to-Assign: clicking the card header arms this card as the target for the next badge scan.
  // The scan is then applied as if the associate were dragged onto the card. Clicking again disarms.
  const isArmedForScan = Boolean(armedScanCard && armedScanCard.boardEntry === boardEntry.id);
  // The card reads as "selected" when the card itself is armed OR one of its slots is armed, so
  // clicking a slot also selects (highlights) the whole card rectangle.
  const hasArmedSlotOnCard = Boolean(armedScanSlot && armedScanSlot.boardEntry === boardEntry.id);
  const isSelected = isArmedForScan || hasArmedSlotOnCard;
  const onToggleArmCard = () => {
    if (!setArmedScanCard) {
      return;
    }
    setArmedScanCard(isArmedForScan ? null : { boardEntry: boardEntry.id, title: boardEntry.title });
  };
  // Keyboard equivalent of the click, so the card header is operable without a mouse.
  const onToggleArmCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggleArmCard();
    }
  };

  const renderProcessDetailCard = () => {
    // Per-process running ordinal: a slot's id is its position WITHIN its process path
    // (0..required-1), not a global render index. Shared with ShiftAllocationAssociatesView via
    // createSlotIdGenerator so both derive identical slot ids. Filled assignments of a process are
    // counted first (they render first), then that process's empty slots continue the ordinal.
    const nextSlotIdForProcess = createSlotIdGenerator(boardEntry.id);

    const renderList = boardEntry.assignments.map((assignment, index) => {
      const slotId = nextSlotIdForProcess(assignment.allocationAttrs.process);
      const customLabel = slotLabels[slotId];
      const defaultHeader =
        assignment.allocationAttrs.type === ALLOCATION_TYPE.CLUSTERS
          ? clusterAssignmentHeaderFormatter(assignment)
          : null;
      return (
        <Draggable
          // adding a key is important!
          key={assignment.resourceId}
          draggableId={assignment.resourceId}
          index={index}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              id={`${SHIFT_ALLOCATION_ID_PREFIX.DRAGGABLE_ASSOCIATE}-${assignment.resourceId}`}
              style={{ ...provided.draggableProps.style, position: snapshot.isDragging ? 'absolute' : 'static' }}>
              <EmployeeIcon
                boardEntry={boardEntry}
                associate={associateInfo[assignment.resourceId]}
                assignedProcess={assignment.allocationAttrs.process}
                header={customLabel != null ? customLabel : defaultHeader}
                editableHeader={isLabelEditable}
                onEditHeader={isLabelEditable ? makeOnEditLabel(slotId) : undefined}
              />
            </div>
          )}
        </Draggable>
      );
    });

    getHeadcountForProcessPaths(boardEntry).forEach((headCount, index) => {
      if (headCount.actualHeadcount < headCount.required) {
        const missing = headCount.required - headCount.actualHeadcount;
        for (let i = 0; i < missing; i++) {
          const slotId = nextSlotIdForProcess(headCount.process);
          const customLabel = slotLabels[slotId];
          const droppableId = `${boardEntry.id}${DROPPABLE_ID_SEPARATOR}${headCount.process}${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.RECOMMENDED_SPOT_DROP}${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.PROCESS_DETAIL_CARD}${DROPPABLE_ID_SEPARATOR}${i}`;
          renderList.push(
            <Droppable
              droppableId={droppableId}
              type={'PROCESS_PATH'}
              key={`${boardEntry.id}-${headCount.process}-${i}`}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  id={`${SHIFT_ALLOCATION_ID_PREFIX.EMPTY_ASSOCIATE_PREFIX}-${droppableId}`}>
                  <EmptyEmployeeIcon
                    header={customLabel != null ? customLabel : headCount.process}
                    boardEntry={boardEntry.id}
                    containerType={SHIFT_ALLOCATION_ID_PREFIX.PROCESS_CARD_ID_PREFIX}
                    processPathId={headCount.process}
                    droppableId={droppableId}
                    placeHolder={provided.placeholder}
                    editableHeader={isLabelEditable}
                    onEditHeader={isLabelEditable ? makeOnEditLabel(slotId) : undefined}
                  />
                </div>
              )}
            </Droppable>
          );
        }
      }
    });

    if (validProcessToRenderProcessDetailCard(renderList, boardEntry)) {
      const processPathId = boardEntry.processPathEntries[0].process;
      const droppableId = `${boardEntry.id}${DROPPABLE_ID_SEPARATOR}${processPathId}${DROPPABLE_ID_SEPARATOR}${
        SHIFT_ALLOCATION_ID_SUFFIX.RECOMMENDED_SPOT_DROP
      }${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.PROCESS_DETAIL_CARD}${DROPPABLE_ID_SEPARATOR}${0}`;
      renderList.push(
        <Droppable droppableId={droppableId} type={'PROCESS_PATH'} key={`${boardEntry.id}-${processPathId}-${0}`}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              id={`${SHIFT_ALLOCATION_ID_PREFIX.EMPTY_ASSOCIATE_PREFIX}-${droppableId}`}>
              <EmptyEmployeeIcon
                header={processPathId}
                boardEntry={boardEntry.id}
                containerType={SHIFT_ALLOCATION_ID_PREFIX.PROCESS_CARD_ID_PREFIX}
                processPathId={processPathId}
                droppableId={droppableId}
                placeHolder={provided.placeholder}
              />
            </div>
          )}
        </Droppable>
      );
    }

    // special case for Trainee and Yard Marshall
    if (EXCLUSIVE_PROCESS_DETAIL_CARDS.includes(boardEntry.title)) {
      const processNames = boardEntry.processPathEntries.map((entry) => entry.process);
      processNames.forEach((process) => {
        const processPathId = process;
        const droppableId = `${boardEntry.id}${DROPPABLE_ID_SEPARATOR}${processPathId}${DROPPABLE_ID_SEPARATOR}${
          SHIFT_ALLOCATION_ID_SUFFIX.RECOMMENDED_SPOT_DROP
        }${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.PROCESS_DETAIL_CARD}${DROPPABLE_ID_SEPARATOR}${0}`;
        renderList.push(
          <Droppable droppableId={droppableId} type={'PROCESS_PATH'} key={`${boardEntry.id}-${processPathId}-${0}`}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                id={`${SHIFT_ALLOCATION_ID_PREFIX.EMPTY_ASSOCIATE_PREFIX}-${droppableId}`}>
                <EmptyEmployeeIcon
                  header={processPathId}
                  boardEntry={boardEntry.id}
                  containerType={SHIFT_ALLOCATION_ID_PREFIX.PROCESS_CARD_ID_PREFIX}
                  processPathId={processPathId}
                  droppableId={droppableId}
                  placeHolder={provided.placeholder}
                />
              </div>
            )}
          </Droppable>
        );
      });
    }
    return renderList;
  };

  return (
    <div
      style={{
        borderRadius: 4,
        outline: isSelected ? SCAN_ARMED_OUTLINE : 'none',
        boxShadow: isSelected ? SCAN_ARMED_GLOW : 'none'
      }}>
      <Column spacingInset="none" spacing="none" id={'test_row'} type={'outline'}>
        <div
          onClick={onToggleArmCard}
          onKeyDown={onToggleArmCardKeyDown}
          role="button"
          tabIndex={0}
          data-scan-target="true"
          aria-pressed={isArmedForScan}
          title={
            isArmedForScan ? 'Waiting for scan… click to cancel' : 'Click, then scan a badge to assign to this card'
          }
          style={{
            backgroundColor: getProcessCardHeaderColor(boardEntry, associateInfo),
            minHeight: '20px',
            alignmentHorizontal: 'center',
            cursor: 'pointer'
          }}>
          <Text color="inverted" alignment="center" className="clusterDetailCardTitle" type={'h100'}>
            {boardEntry.title}
          </Text>
        </div>
        <Droppable
          droppableId={`${boardEntry.id}${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.PROCESS_DETAIL_CARD}`}
          type={'PROCESS_PATH'}
          key={boardEntry.id}>
          {(provided, snapshot) => (
            <div ref={provided.innerRef} style={{ width: '100%' }} {...provided.droppableProps}>
              <Row
                alignmentHorizontal="start"
                spacingInset="300"
                wrap={'down'}
                spacing="200"
                widths={['grid-4']}
                id={`${SHIFT_ALLOCATION_ID_PREFIX.PROCESS_CARD_ID_PREFIX}-${boardEntry.id}${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.PROCESS_DETAIL_CARD}`}>
                {renderProcessDetailCard()}
              </Row>
              <div style={{ height: 0, overflow: 'hidden' }}>{provided.placeholder}</div>
            </div>
          )}
        </Droppable>
      </Column>
    </div>
  );
};
